import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { collect } from 'collect.js';
import { Button } from 'antd';
import FadeIn from '../FadeIn';
import PriceInfo from '../contract/PriceInfo';
import LoadingIndicator from '../LoadingIndicator';
import useApiClient from '../../hooks/api/useApiClient';
import useForm from '../../hooks/utility/useForm';
import useFeedbackLabel from '../../hooks/ui/useFeedbackLabel';
import { BillingMode } from '../../services/exports/Constants';
import SignedContract from '../contract/SignedContract';
import YousignContractFlow from '../contract/yousign/YousignContractFlow';
import usePermissions from '../../hooks/auth/usePermissions';
import { persistedContractDiscount } from '../../services/helpers/ContractDiscountHelpers';
import SignStep from '../contract/SignStep';

const SkipAllowedEmails = ['oguzhan@foodamigos.io', 'artur@foodamigos.io'];

export default function ContractStep({ moveForward, isSkippable, skip, skipping }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:ContractStep' });

  const { ServiceContractsManager } = useApiClient();

  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  const profile = useSelector((state) => state.initialData.profile);
  const { isAdmin } = usePermissions();

  const canSkip = isSkippable && (isAdmin || SkipAllowedEmails.includes(profile?.email));

  const setupPackages = serviceProvider.setup_packages;
  const hasSetupFee = useMemo(
    () =>
      collect(setupPackages)
        .filter((item) => item.stripe_billing_price !== 0 || item.internal_invoice_billing_price !== 0)
        .isNotEmpty(),
    [setupPackages],
  );

  const [contract, setContract] = useState(null);
  // When the service contract carries a non-zero annual-contract discount the
  // setup-package prices are shown discounted (struck original + "Was …"
  // subtext). 0/null → list price. Persisted values stay undiscounted. The
  // stored percentage is shown as the backend bills it, never capped to
  // today's grant range.
  const effectiveDiscount = persistedContractDiscount(contract?.contract_discount_percentage);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [activeStepIndex, setActiveStepIndex] = useState(hasSetupFee ? 0 : 1);
  const [mounted, setMounted] = useState(false);
  const { setErrorFeedback, renderFeedbackLabel } = useFeedbackLabel();

  const { data, update, setData } = useForm({
    store_quantity: contract?.store_quantity ?? 1,
    billing_mode:
      contract?.billing_mode ?? (serviceProvider?.is_master ? BillingMode.Stripe : BillingMode.InternalInvoicing),
    setup_package_id: contract?.setup_package_id ?? setupPackages[0]?.id,
  });

  useEffect(() => {
    loadContract().then(() => setMounted(true));
  }, []);

  const loadContract = async (withLoading = true) => {
    const onResponse = (success, data) => {
      withLoading && setLoading(false);

      if (!success) {
        toast.error(t('toasts.failed_to_load_data'));

        return null;
      }

      setContract(data);
      setData({
        store_quantity: data.store_quantity,
        billing_mode: data.billing_mode,
        setup_package_id: data.setup_package_id,
      });

      // Restore the sub-step on refresh: if the contract already has a package
      // selected, skip package selection and go straight to the contract/sign
      // step instead of forcing a re-select (which would regenerate it).
      if (hasSetupFee && data.setup_package_id) {
        setActiveStepIndex(1);
      }

      return data;
    };

    withLoading && setLoading(true);
    const { success, data } = await ServiceContractsManager.get();

    if (!data?.id) {
      const { success, data } = await ServiceContractsManager.save();

      return onResponse(success, data);
    }

    return onResponse(success, data);
  };

  const updateContract = async (extra = {}) => {
    setUpdating(true);
    const { success, data: responseData } = await ServiceContractsManager.save({
      ...data,
      ...extra,
    });
    setUpdating(false);

    if (!success) {
      setErrorFeedback(responseData);

      return toast.error(t('toasts.failed_to_save_changes'));
    }

    return setContract(responseData);
  };

  const onSetupPackageSelection = (setupPackage) => {
    update('setup_package_id', setupPackage.id);

    return updateContract({ setup_package_id: setupPackage.id }).then(() => setActiveStepIndex(1));
  };

  function onContractSigned(data) {
    setContract(data);

    // Immediate payment (when required) is now collected in the separate
    // ContractPaymentStep, so the contract step always advances once signed.
    moveForward();
  }

  if (loading || !mounted) {
    return <LoadingIndicator />;
  }

  return (
    <FadeIn>
      {contract?.signed_at ? (
        <SignedContract data={contract} />
      ) : serviceProvider?.is_master ? (
        <YousignContractFlow
          contract={contract}
          setContract={setContract}
          onSuccess={onContractSigned}
          isSkippable={canSkip}
          skip={skip}
          skipping={skipping}
        />
      ) : (
        <>
          {hasSetupFee && activeStepIndex === 0 && (
            <>
              {renderFeedbackLabel}
              <PriceInfo
                data={data}
                loading={updating}
                setupPackages={setupPackages}
                discount={effectiveDiscount}
                onChange={update}
                onSetupPackageSelection={onSetupPackageSelection}
              />
            </>
          )}
          {contract && activeStepIndex !== 0 && (
            <SignStep
              contract={contract}
              onSuccess={onContractSigned}
              backButton={<Button onClick={() => setActiveStepIndex(0)}>{t('buttons.change_plan')}</Button>}
            />
          )}
        </>
      )}
    </FadeIn>
  );
}
